import React, { useState, useEffect, useCallback, useRef, useMemo, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import config from "../../config.js";
import SubTitle from "../common/SubTitle";
import SearchFilterWithDatePicker from "./SearchFilterWithDatePicker";
import EntryExitTable from "./EntryExitTable";
import CreateEntryExitForm from "./CreateEntryExitForm";
import { useUser } from "../../pages/login/UserContextPersistent";
import { usePermission } from "../../hooks/usePermission";
import { useTranslation } from "react-i18next";
import { downloadBackendExcel } from "../../utils/exportTableData";

function getOrgId() {
  try { return JSON.parse(localStorage.getItem("userState") || "{}").user?.organizationId; }
  catch { return null; }
}
function getOrgName() {
  try { return JSON.parse(localStorage.getItem("userState") || "{}")?.organization?.name || ""; }
  catch { return ""; }
}

// Prisma VehicleType enum values — keep in sync with CreateEntryExitForm
const VEHICLE_TYPE_OPTIONS = [
  { value: "CAR", label: "Car" },
  { value: "TRUCK", label: "Truck" },
  { value: "VAN", label: "Van" },
  { value: "BUS", label: "Bus" },
  { value: "BIKE", label: "Bike" },
  { value: "OTHER", label: "Other" },
];

function fmtDate(dt) {
  if (!dt) return "N/A";
  return new Date(dt).toLocaleDateString("en-GB");
}
function fmtTime(dt) {
  if (!dt) return "N/A";
  return new Date(dt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
}
function fmtDuration(mins) {
  if (mins == null) return "N/A";
  const h = Math.floor(mins / 60), m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/** Pretty-print stored enum-style snapshots (TRUCK → Truck) for the register table */
function fmtVehicleTypeLabel(raw) {
  if (raw == null || String(raw).trim() === "") return "—";
  const s = String(raw).trim();
  if (/^[A-Z][A-Z0-9_]*$/.test(s)) {
    return s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, " ");
  }
  return s;
}

// Format a JS Date into local-day boundaries in ISO form.
// Backend uses `new Date(queryValue)` and compares DateTimes, so we pass actual timestamps.
function toLocalDayStartISO(d) {
  if (!d) return null;
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  const y = dt.getFullYear();
  const m = dt.getMonth();
  const day = dt.getDate();
  return new Date(y, m, day, 0, 0, 0, 0).toISOString();
}

function toLocalDayEndISO(d) {
  if (!d) return null;
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  const y = dt.getFullYear();
  const m = dt.getMonth();
  const day = dt.getDate();
  return new Date(y, m, day, 23, 59, 59, 999).toISOString();
}

/** Latest VehicleImage URL from vehicle.documents (aligned with CreateEntryExitForm / VehicleDetailsOverlay). */
function getVehicleImageUrlFromVehicle(vehicle) {
  if (!vehicle?.documents?.length) return null;
  const imgs = vehicle.documents.filter((d) => d.name === "VehicleImage");
  if (!imgs.length) return null;
  imgs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const doc = imgs[0];
  return doc?.location
    ? `${config.baseURL}/upload/image?fileName=${encodeURIComponent(doc.location)}`
    : null;
}

function transformLog(log) {
  const hasExit = log.status === "COMPLETED";
  const vehicleImage = getVehicleImageUrlFromVehicle(log.vehicle);
  return {
    id: log.id,
    vehicleName: log.vehicleNameSnapshot || log.vehicle?.name || "—",
    vehicleNumber: log.vehicleNumberSnapshot || "—",
    type: fmtVehicleTypeLabel(log.vehicleTypeSnapshot),
    entryDate: fmtDate(log.entry?.dateTime),
    entryTime: fmtTime(log.entry?.dateTime),
    entryGate: log.entry?.gateName || "N/A",
    exitDate: hasExit ? fmtDate(log.exit?.dateTime) : "N/A",
    exitTime: hasExit ? fmtTime(log.exit?.dateTime) : "N/A",
    exitGate: hasExit ? (log.exit?.gateName || "N/A") : "N/A",
    duration: fmtDuration(log.durationMinutes),
    driver: log.driverNameSnapshot || "N/A",
    driverPhone: log.driverPhoneSnapshot || "N/A",
    hasExit,
    vehicleImage,
    comments: log.comments || "",
    status: log.status,
    _logId: log.id,
    _exitSource: log.exit?.source || null,
  };
}

export default function VehicleEntryExit() {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationInactive } = useUser();
  const { t } = useTranslation();
  const { hasDownloadPermission } = usePermission();
  const organizationId = getOrgId();
  const orgName = getOrgName();
  const canCreateRecord = !organizationInactive;
  const canUserDownload = hasDownloadPermission("vehicle-entry-exit");

  const [logs, setLogs] = useState([]);
  const [searchBaseLogs, setSearchBaseLogs] = useState([]);
  const [rawLogs, setRawLogs] = useState([]);
  const [selectedLogIds, setSelectedLogIds] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });
  const [gates, setGates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(
    Boolean(location.state?.openCreateForm) && !organizationInactive,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [apiFilters, setApiFilters] = useState({
    vehicleType: "", entryGateId: "", exitGateId: "", entryDate: null, exitDate: null,
  });
  const refreshTimer = useRef(null);
  const SEARCH_FETCH_LIMIT = 5000;
  const isSearchMode = searchQuery.trim().length > 0;

  // Note: Vehicle Type dropdown is intentionally static to ensure it is not affected by pagination.

  const fetchLogs = useCallback(async (page = 1, options = {}) => {
    const { fetchAllForSearch = false } = options;
    if (!organizationId) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        organizationId,
        page: fetchAllForSearch ? 1 : page,
        limit: fetchAllForSearch ? SEARCH_FETCH_LIMIT : pagination.limit,
      });
      if (apiFilters.vehicleType) params.set("vehicleType", apiFilters.vehicleType);
      if (apiFilters.entryGateId) params.set("entryGateId", apiFilters.entryGateId);
      if (apiFilters.exitGateId) params.set("exitGateId", apiFilters.exitGateId);
      // Treat the selected dates as "exact calendar day" (start..end), not "from that date onward".
      if (apiFilters.entryDate) {
        const fromISO = toLocalDayStartISO(apiFilters.entryDate);
        const toISO = toLocalDayEndISO(apiFilters.entryDate);
        if (fromISO) params.set("entryDateFrom", fromISO);
        if (toISO) params.set("entryDateTo", toISO);
      }
      if (apiFilters.exitDate) {
        const fromISO = toLocalDayStartISO(apiFilters.exitDate);
        const toISO = toLocalDayEndISO(apiFilters.exitDate);
        if (fromISO) params.set("exitDateFrom", fromISO);
        if (toISO) params.set("exitDateTo", toISO);
      }
      const res = await axios.get(`${config.baseURL}/vehicle-log?${params}`, { withCredentials: true });
      const raw = res.data.data || [];
      if (fetchAllForSearch) {
        setSearchBaseLogs(raw.map(transformLog));
      } else {
        setRawLogs(raw);
        setLogs(raw.map(transformLog));
        setPagination(res.data.pagination);
      }
    } catch { toast.error("Failed to load entry/exit records"); }
    finally { setIsLoading(false); }
  }, [organizationId, apiFilters, pagination.limit]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!organizationId) return;
    if (isSearchMode) {
      fetchLogs(1, { fetchAllForSearch: true });
      setPagination((prev) => ({ ...prev, page: 1 }));
      return;
    }
    fetchLogs(1);
  }, [apiFilters, organizationId, isSearchMode]);

  useEffect(() => {
    if (!organizationId) return;
    axios.get(`${config.baseURL}/stop/gates?organizationId=${organizationId}`, { withCredentials: true })
      .then(r => setGates(r.data || [])).catch(() => {});
  }, [organizationId]);

  // When navigated here from the Analytical View "Create Record" button,
  // open the form and clear the flag so it doesn't reopen on refresh/back.
  useEffect(() => {
    if (location.state?.openCreateForm && canCreateRecord) {
      setShowForm(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, canCreateRecord]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    refreshTimer.current = setInterval(() => {
      if (isSearchMode) {
        fetchLogs(1, { fetchAllForSearch: true });
      } else {
        fetchLogs(pagination.page);
      }
    }, 30000);
    return () => clearInterval(refreshTimer.current);
  }, [pagination.page, apiFilters, isSearchMode, fetchLogs]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    if (!isSearchMode) {
      setSearchBaseLogs([]);
    }
  }, [searchQuery, isSearchMode]);

  const filterConfig = useMemo(() => [
    {
      id: "vehicleType",
      label: "Vehicle Type",
      options: VEHICLE_TYPE_OPTIONS,
    },
    {
      id: "entryGateId",
      label: "Entry Gate",
      options: gates.map(g => ({ value: String(g.id), label: g.name })),
    },
    {
      id: "exitGateId",
      label: "Exit Gate",
      options: gates.map(g => ({ value: String(g.id), label: g.name })),
    },
    { id: "entryDate", label: "Entry Date", type: "date" },
    { id: "exitDate", label: "Exit Date", type: "date" },
  ], [gates]);

  const handleFilterChange = (values) => {
    setApiFilters({
      vehicleType: values.vehicleType || "",
      entryGateId: values.entryGateId || "",
      exitGateId: values.exitGateId || "",
      entryDate: values.entryDate || null,
      exitDate: values.exitDate || null,
    });
  };

  const filteredLogs = useMemo(() => {
    const sourceLogs = isSearchMode ? searchBaseLogs : logs;
    if (!searchQuery) return sourceLogs;
    const q = searchQuery.toLowerCase();
    return sourceLogs.filter((l) => {
      const searchableValues = [
        l.vehicleName,
        l.vehicleNumber,
        l.type,
        l.entryDate,
        l.entryTime,
        l.entryGate,
        l.exitDate,
        l.exitTime,
        l.exitGate,
        l.duration,
      ];
      return searchableValues.some((value) =>
        String(value || "").toLowerCase().includes(q)
      );
    });
  }, [logs, searchBaseLogs, searchQuery, isSearchMode]);

  const paginationForTable = useMemo(() => {
    if (!isSearchMode) return pagination;
    const total = filteredLogs.length;
    const limit = pagination.limit || 20;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const page = Math.min(pagination.page || 1, totalPages);
    return { total, page, limit, totalPages };
  }, [isSearchMode, filteredLogs.length, pagination]);

  const pagedFilteredLogs = useMemo(() => {
    if (!isSearchMode) return filteredLogs;
    const start = (paginationForTable.page - 1) * paginationForTable.limit;
    return filteredLogs.slice(start, start + paginationForTable.limit);
  }, [isSearchMode, filteredLogs, paginationForTable]);

  const handleLogCreated = () => { setShowForm(false); fetchLogs(1); };
  const handleLogUpdated = (logId, nextComment) => {
    if (logId !== undefined) {
      setLogs((prev) =>
        prev.map((row) =>
          row._logId === logId ? { ...row, comments: nextComment || "" } : row
        )
      );
      setSearchBaseLogs((prev) =>
        prev.map((row) =>
          row._logId === logId ? { ...row, comments: nextComment || "" } : row
        )
      );
    }
    fetchLogs(pagination.page, { fetchAllForSearch: isSearchMode });
  };
  const handlePageChange = (p) => {
    if (isSearchMode) {
      setPagination((prev) => ({ ...prev, page: p }));
      return;
    }
    fetchLogs(p);
    setPagination((prev) => ({ ...prev, page: p }));
  };

  const cleanOrgName = (orgName || "RouteEye").replace(/[^a-zA-Z0-9_-]/g, "_");
  const entryExitExportFileName = `${cleanOrgName}_Vehicle_Entry_Exit_Register`;

  const entryExitDownloadHeaders = useMemo(
    () => [
      { label: "Vehicle", key: "vehicleName" },
      { label: "Reg. No", key: "vehicleNumber" },
      { label: "Type", key: "type" },
      { label: "Entry Date", key: "entryDate" },
      { label: "Entry Time", key: "entryTime" },
      { label: "Entry Gate", key: "entryGate" },
      { label: "Exit Date", key: "exitDate" },
      { label: "Exit Time", key: "exitTime" },
      { label: "Exit Gate", key: "exitGate" },
      { label: "Duration", key: "duration" },
      { label: "Driver", key: "driver" },
      { label: "Driver Phone", key: "driverPhone" },
      { label: "Comments", key: "comments" },
    ],
    []
  );

  const handleDownloadAllRecords = useCallback(async () => {
    if (!organizationId) return [];
    const downloadFileName = `${entryExitExportFileName}.xlsx`;
    const downloadParams = {
      organizationId,
      table: "vehicle_logs",
      format: "xlsx",
      includeDetails: true,
      vehicleType: apiFilters.vehicleType || undefined,
      entryGateId: apiFilters.entryGateId || undefined,
      exitGateId: apiFilters.exitGateId || undefined,
    };
    if (selectedLogIds && selectedLogIds.length > 0) {
      downloadParams.ids = selectedLogIds.join(",");
      downloadParams.logIds = selectedLogIds.join(",");
    }

    try {
      await downloadBackendExcel({
        url: `${config.baseURL}/excel/download`,
        params: downloadParams,
        defaultFileName: downloadFileName,
      });
      return [];
    } catch (err) {
      console.warn("Backend /excel/download table=vehicle_logs failed, trying table=vehicle-log:", err);
      try {
        await downloadBackendExcel({
          url: `${config.baseURL}/excel/download`,
          params: { ...downloadParams, table: "vehicle-log" },
          defaultFileName: downloadFileName,
        });
        return [];
      } catch (fallbackErr) {
        console.warn("Backend export not available yet, fetching full data for client download:", fallbackErr);
        try {
          const params = new URLSearchParams({
            organizationId,
            page: 1,
            limit: 10000,
          });
          if (apiFilters.vehicleType) params.set("vehicleType", apiFilters.vehicleType);
          if (apiFilters.entryGateId) params.set("entryGateId", apiFilters.entryGateId);
          if (apiFilters.exitGateId) params.set("exitGateId", apiFilters.exitGateId);
          if (apiFilters.entryDate) {
            const fromISO = toLocalDayStartISO(apiFilters.entryDate);
            const toISO = toLocalDayEndISO(apiFilters.entryDate);
            if (fromISO) params.set("entryDateFrom", fromISO);
            if (toISO) params.set("entryDateTo", toISO);
          }
          if (apiFilters.exitDate) {
            const fromISO = toLocalDayStartISO(apiFilters.exitDate);
            const toISO = toLocalDayEndISO(apiFilters.exitDate);
            if (fromISO) params.set("exitDateFrom", fromISO);
            if (toISO) params.set("exitDateTo", toISO);
          }
          const res = await axios.get(`${config.baseURL}/vehicle-log?${params}`, { withCredentials: true });
          const raw = res.data.data || [];
          let transformed = raw.map(transformLog);
          if (searchQuery) {
            const q = searchQuery.toLowerCase();
            transformed = transformed.filter((l) => {
              const searchableValues = [
                l.vehicleName,
                l.vehicleNumber,
                l.type,
                l.entryDate,
                l.entryTime,
                l.entryGate,
                l.exitDate,
                l.exitTime,
                l.exitGate,
                l.duration,
                l.driver,
                l.driverPhone,
                l.comments,
              ];
              return searchableValues.some((value) =>
                String(value || "").toLowerCase().includes(q)
              );
            });
          }
          if (selectedLogIds && selectedLogIds.length > 0) {
            transformed = transformed.filter((l) =>
              selectedLogIds.includes(l.id ?? l._logId)
            );
          }
          return transformed;
        } catch {
          toast.error("Failed to export records");
          return [];
        }
      }
    }
  }, [organizationId, apiFilters, searchQuery, selectedLogIds, entryExitExportFileName]);

  const allSelectableLogIds = useMemo(
    () => filteredLogs.map((l) => l.id ?? l._logId),
    [filteredLogs]
  );
  const displayCount = isSearchMode ? filteredLogs.length : (pagination?.total ?? filteredLogs.length);
  const pageTitleWithCount = `Vehicles Entry/Exit Register (${displayCount} Total)`;

  return (
    <>
      <SubTitle
        title={pageTitleWithCount}
        path={`${orgName} > Vehicles > Vehicle Entry/Exit Register`}
        add={canCreateRecord ? "+ Create Record" : ""}
        onAddClick={canCreateRecord ? () => setShowForm(true) : null}
        track="Analytical View"
        trackPath="/vehicle/analytical-EntryExit"
        onTrackClick={() => navigate("/vehicle/analytical-EntryExit")}
      />

      <Box className="table_container">
        {showForm && canCreateRecord && (
          <Box sx={{ mb: "20px" }}>
            <CreateEntryExitForm
              organizationId={organizationId}
              onClose={() => setShowForm(false)}
              onCreated={handleLogCreated}
            />
          </Box>
        )}

        <SearchFilterWithDatePicker
          filters={filterConfig}
          searchPlaceholder="Search..."
          onSearchChange={setSearchQuery}
          onFilterChange={handleFilterChange}
          dataDownload={true}
          downloadRows={
            selectedLogIds.length > 0
              ? filteredLogs.filter((l) => selectedLogIds.includes(l.id ?? l._logId))
              : filteredLogs
          }
          downloadColumns={entryExitDownloadHeaders}
          onDownload={handleDownloadAllRecords}
          downloadFileName={entryExitExportFileName}
          downloadDisabled={selectedLogIds.length === 0}
          downloadTooltipTitle={
            selectedLogIds.length === 0
              ? t("Please select records to download")
              : `${t("Download")} (${selectedLogIds.length})`
          }
          selectedCount={selectedLogIds.length}
        />

        <Suspense fallback={<div className="loader" />}>
          <EntryExitTable
            data={pagedFilteredLogs}
            gates={gates}
            loading={isLoading}
            onLogUpdated={handleLogUpdated}
            pagination={paginationForTable}
            onPageChange={handlePageChange}
            selectable={canUserDownload}
            selectedRowKeys={selectedLogIds}
            onSelectionChange={setSelectedLogIds}
            allSelectableKeys={allSelectableLogIds}
          />
        </Suspense>
      </Box>

      <Box sx={{ height: "20vh" }} />
    </>
  );
}
